// GradeCNPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getAllGrades, deleteGrade } from "../../../services/gradCnServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getBeltHistoryByAdherent,
  getBeltsList,
} from "../../../services/beltServices";
import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import { FaPlusSquare } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import CreateGradeModal from "./CreateGradeModal";
import EditGradeModal from "./EditGradeModal";
import Loader from "./../../../utils/Loader.jsx";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

function GradeCNPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Data
  const [grades, setGrades] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({});
  const [settings, setSettings] = useState({});

  // UI / filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [filterBeltName, setFilterBeltName] = useState(""); // 🔸 filtre par NOM de ceinture actuelle
  const [loading, setLoading] = useState(true);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editableGrade, setEditableGrade] = useState({});

  // Initial fetch (settings + belts + grades + belt histories)
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [gradesData, beltsData, settingsData] = await Promise.all([
          getAllGrades(),
          getBeltsList(),
          getSettings(),
        ]);
        setGrades(gradesData || []);
        setBelts(beltsData || []);

        const fetchedSettings = settingsData?.[0] || {};
        setSettings(fetchedSettings);

        if (fetchedSettings?.saison_active) {
          setSelectedSeason(fetchedSettings.saison_active);
          setSeasons([
            getPreviousSeason(fetchedSettings.saison_active),
            fetchedSettings.saison_active,
            getNextSeason(fetchedSettings.saison_active),
          ]);
        } else {
          // Fallback : pas de saison_active => on propose "Toutes les saisons"
          setSelectedSeason("");
          setSeasons([]);
        }

        // Dernier passage par adhérent
        const beltHistoryPromises = (gradesData || []).map((g) =>
          getBeltHistoryByAdherent(g.adherent_id)
        );
        const results = await Promise.all(beltHistoryPromises);
        const map = {};
        results.forEach((h, idx) => {
          map[gradesData[idx].adherent_id] = h?.[0] || null;
        });
        setBeltHistories(map);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        toast.error("Erreur lors du chargement des données.");
        setGrades([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getPreviousSeason = (currentSeason) => {
    const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
    if (!startYear || !endYear) return "";
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
    if (!startYear || !endYear) return "";
    return `${startYear + 1}-${endYear + 1}`;
  };

  // 🔸 Map id -> nom pour les ceintures
  const beltIdToName = useMemo(() => {
    const obj = {};
    (belts || []).forEach((b) => {
      obj[b.id] = b.nom;
    });
    return obj;
  }, [belts]);

  // 🔸 Options dynamiques de ceintures présentes (triées par belt_id asc)
  const presentBeltNames = useMemo(() => {
    const ids = new Set();
    (grades || []).forEach((g) => {
      if (selectedSeason && g?.Adherent?.saison_active !== selectedSeason)
        return;
      const last = beltHistories[g.adherent_id];
      if (last?.belt_id != null) ids.add(Number(last.belt_id));
    });
    return Array.from(ids)
      .sort((a, b) => a - b)
      .map((id) => beltIdToName[id])
      .filter(Boolean);
  }, [grades, beltHistories, selectedSeason, beltIdToName]);

  // Filtrage principal
  const filteredGrades = useMemo(
    () =>
      (grades || []).filter((grade) => {
        const n = `${grade?.Adherent?.nom || ""}`.toLowerCase();
        const p = `${grade?.Adherent?.prenom || ""}`.toLowerCase();
        const q = searchTerm.trim().toLowerCase();
        const matchSearch = !q || n.includes(q) || p.includes(q);

        const matchSeason =
          !selectedSeason || grade?.Adherent?.saison_active === selectedSeason;

        // Filtre Ceinture actuelle (sur NOM de ceinture)
        const lastHist = beltHistories[grade.adherent_id];
        const currentBeltName =
          lastHist?.belt_id != null
            ? beltIdToName[lastHist.belt_id]
            : undefined;
        const matchBelt = !filterBeltName || currentBeltName === filterBeltName;

        return matchSearch && matchSeason && matchBelt;
      }),
    [
      grades,
      searchTerm,
      selectedSeason,
      filterBeltName,
      beltHistories,
      beltIdToName,
    ]
  );

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSeasonChange = (e) => setSelectedSeason(e.target.value);
  const handleBeltChange = (e) => setFilterBeltName(e.target.value);

  const openEditModal = (grade) => {
    setEditableGrade(grade);
    setIsModalOpen(true);
  };
  const closeEditModal = async () => {
    await refreshGradesAndHistories();
    setIsModalOpen(false);
  };

  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = async () => {
    await refreshGradesAndHistories();
    setIsCreateModalOpen(false);
  };

  const refreshGradesAndHistories = async () => {
    try {
      setLoading(true);
      const data = await getAllGrades();
      setGrades(data || []);
      const results = await Promise.all(
        (data || []).map((g) => getBeltHistoryByAdherent(g.adherent_id))
      );
      const map = {};
      results.forEach((h, idx) => {
        map[data[idx].adherent_id] = h?.[0] || null;
      });
      setBeltHistories(map);
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
      toast.error("Erreur lors du rafraîchissement.");
    } finally {
      setLoading(false);
    }
  };

  const deleteGradeHandler = async (id) => {
    try {
      await deleteGrade(id);
      await refreshGradesAndHistories();
      toast.success("Grade supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression du grade", error);
      toast.error("Erreur lors de la suppression du grade");
    }
  };

  const renderUvIcon = (value) => (value === 1 ? "✅" : "❌");

  const getBeltPhoto = (beltId) => {
    const belt = belts.find((b) => b.id === beltId);
    return belt
      ? `/img/belt/${belt.belt_url}`
      : "/img/belt/ceinture_inconnue.png";
  };

  const getAdherentPhoto = (adherent) =>
    adherent?.photo_url ? adherent.photo_url : getDefaultPhoto(adherent?.genre);

  const getNextBeltPhoto = (currentBeltId) => {
    const idx = belts.findIndex((b) => b.id === currentBeltId);
    if (idx === -1 || idx + 1 >= belts.length)
      return "/img/belt/ceinture_inconnue.png";
    return `/img/belt/${belts[idx + 1].belt_url}`;
  };

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString("fr-FR") : "—";

  // --------------------------------
  // RENDER
  // --------------------------------
  return (
<section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">      {/* Titres de page */}
              <HeaderTitle
                role="Enseignant"
                title="Grade Ceinture Noire"
                align="center"
                className="mb-1"
              />

      <DashboardNavigation role={userRole} />
      <ScrollIndicator />

      {/* Carte principale */}
      <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mx-3 sm:mx-6 mt-4">
        {/* Bandeau dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 text-white">
          <h3 className="text-lg sm:text-xl font-semibold text-center">
            Gestion des Grades de Ceinture Noire (CN)
          </h3>
          <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center">
            Filtre par saison (&ldquo;Toutes les saisons&rdquo;) • par ceinture
            actuelle • recherche par nom. Édite, supprime, ou crée un grade CN.
          </p>
        </div>

        {/* Toolbar filtres + action */}
        <div className="px-5 pt-5 pb-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Rechercher par nom ou prénom…"
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={searchTerm}
              onChange={handleSearchChange}
            />

            <select
              name="selectedSeason"
              value={selectedSeason}
              onChange={handleSeasonChange}
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              <option value="">Toutes les saisons</option>
              {seasons.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>

            {/* 🔸 Filtre "Ceinture actuelle" basé sur les ceintures présentes */}
            <select
              name="filterBeltName"
              value={filterBeltName}
              onChange={handleBeltChange}
              className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              <option value="">Toutes les ceintures</option>
              {presentBeltNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Résumé + bouton action à droite */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-slate-700 dark:text-slate-300">
              <span className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1">
                <span className="mr-2 opacity-70">Résultats</span>
                <span className="font-semibold">{filteredGrades.length}</span>
              </span>
            </div>

            <Button
              text="Créer un Grade"
              onClick={openCreateModal}
              icon={FaPlusSquare}
              className="gap-2"
              hoverLabel="Créer un grade"
            />
          </div>
        </div>

        {/* Tableau (desktop) */}
        <div className="px-5 pb-6 sm:px-8 hidden md:block">
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                  {[
                    "Adhérent",
                    "Nom Prénom",
                    "Dernier passage",
                    "Ceinture (actuelle)",
                    "Objectif",
                    "Dominante",
                    "UV1",
                    "UV2",
                    "UV3 pts",
                    "UV3",
                    "UV4",
                    "Observations",
                    "Actions",
                  ].map((h, idx) => (
                    <th
                      key={idx}
                      className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                {filteredGrades.map((grade) => {
                  const adh = grade.Adherent || {};
                  const hist = beltHistories[grade.adherent_id];
                  const currentBeltPhoto = hist
                    ? getBeltPhoto(hist.belt_id)
                    : "/img/belt/ceinture_inconnue.png";
                  const nextBeltPhoto = hist
                    ? getNextBeltPhoto(hist.belt_id)
                    : "/img/belt/ceinture_inconnue.png";
                  const beltDate = hist ? formatDate(hist.date_passage) : "—";
                  const photo = getAdherentPhoto(adh);

                  return (
                    <tr
                      key={grade.id}
                      className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-3 py-3">
                        <img
                          src={photo}
                          alt={`${adh?.prenom || ""} ${adh?.nom || ""}`}
                          className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          onError={(e) =>
                            (e.currentTarget.src = "/img/user_avatar.jpg")
                          }
                        />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">{`${
                        adh?.nom || ""
                      } ${adh?.prenom || ""}`}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {beltDate}
                      </td>
                      <td className="px-3 py-3">
                        <img
                          src={currentBeltPhoto}
                          alt="Ceinture actuelle"
                          className="h-8 object-cover"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <img
                          src={nextBeltPhoto}
                          alt="Ceinture supérieure"
                          className="h-8 object-cover"
                        />
                      </td>
                      <td className="px-3 py-3">{grade.dominante || "—"}</td>
                      <td className="px-3 py-3">{renderUvIcon(grade.uv1)}</td>
                      <td className="px-3 py-3">{renderUvIcon(grade.uv2)}</td>
                      <td className="px-3 py-3">
                        {Number(grade.uv3_points) || 0}
                      </td>
                      <td className="px-3 py-3">{renderUvIcon(grade.uv3)}</td>
                      <td className="px-3 py-3">{renderUvIcon(grade.uv4)}</td>
                      <td className="px-3 py-3 max-w-[260px]">
                        <span className="line-clamp-2">
                          {grade.observations || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex gap-2">
                          <ButtonEdit
                            onClick={() => openEditModal(grade)}
                            hoverLabel="Éditer"
                          />
                          <ButtonDelete
                            onClick={() => deleteGradeHandler(grade.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredGrades.length === 0 && !loading && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                      colSpan={13}
                    >
                      Aucun résultat avec ces filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="py-6">
              <Loader />
            </div>
          )}
        </div>

        {/* Cards (mobile) */}
        <div className="px-5 pb-6 sm:px-8 md:hidden grid grid-cols-1 gap-4">
          {filteredGrades.map((grade) => {
            const adh = grade.Adherent || {};
            const hist = beltHistories[grade.adherent_id];
            const currentBeltPhoto = hist
              ? getBeltPhoto(hist.belt_id)
              : "/img/belt/ceinture_inconnue.png";
            const nextBeltPhoto = hist
              ? getNextBeltPhoto(hist.belt_id)
              : "/img/belt/ceinture_inconnue.png";
            const beltDate = hist ? formatDate(hist.date_passage) : "—";
            const photo = getAdherentPhoto(adh);

            return (
              <div
                key={grade.id}
                className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 transition hover:shadow-lg"
              >
                {/* Header */}
                <div className="bg-slate-50/70 dark:bg-slate-800/40 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={photo}
                      alt={`Photo de ${adh?.prenom || ""} ${adh?.nom || ""}`}
                      className="w-14 h-14 object-cover rounded-full ring-1 ring-slate-200 dark:ring-slate-700"
                      onError={(e) =>
                        (e.currentTarget.src = "/img/user_avatar.jpg")
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold dark:text-gray-100 truncate">{`${
                        adh?.nom || ""
                      } ${adh?.prenom || ""}`}</div>
                      <div className="text-[11px] text-gray-700 dark:text-gray-300 mt-0.5">
                        <span className="inline-flex items-center rounded-md px-2 py-0.5 bg-white/70 dark:bg-slate-900/60 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                          Dernier passage :{" "}
                          <span className="ml-1 font-semibold">{beltDate}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ceintures */}
                <div className="px-4 pt-3">
                  <div className="flex items-center justify-between">
                    <img
                      src={currentBeltPhoto}
                      alt="Ceinture actuelle"
                      className="h-8 object-cover"
                    />
                    <span className="mx-3 font-bold dark:text-gray-100">›</span>
                    <img
                      src={nextBeltPhoto}
                      alt="Ceinture supérieure"
                      className="h-8 object-cover"
                    />
                  </div>
                </div>

                {/* Infos + mini tableau UV */}
                <div className="px-4 pb-4 pt-3">
                  {/* Ligne Dominante */}
                  <div className="text-sm dark:text-gray-100 mb-2">
                    <span className="font-semibold">Dominante:&nbsp;</span>
                    {grade.dominante || "—"}
                  </div>

                  {/* Tableau UV */}
                  <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-xs sm:text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200">
                          <th className="px-2 py-2 text-left font-semibold dark:text-slate-200">
                            UV1
                          </th>
                          <th className="px-2 py-2 text-left font-semibold dark:text-slate-200">
                            UV2
                          </th>
                          <th className="px-2 py-2 text-left font-semibold dark:text-slate-200">
                            Points
                          </th>
                          <th className="px-2 py-2 text-left font-semibold dark:text-slate-200">
                            UV3
                          </th>
                          <th className="px-2 py-2 text-left font-semibold dark:text-slate-200">
                            UV4
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        <tr className="text-slate-800 dark:text-slate-100">
                          <td className="px-2 py-2">
                            {renderUvIcon(grade.uv1)}
                          </td>
                          <td className="px-2 py-2">
                            {renderUvIcon(grade.uv2)}
                          </td>
                          <td className="px-2 py-2">
                            {Number(grade.uv3_points) || 0}
                          </td>
                          <td className="px-2 py-2">
                            {renderUvIcon(grade.uv3)}
                          </td>
                          <td className="px-2 py-2">
                            {renderUvIcon(grade.uv4)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Observations */}
                  <div className="text-sm dark:text-gray-100 mt-3">
                    <span className="font-semibold">Observations:&nbsp;</span>
                    <span className="line-clamp-3">
                      {grade.observations || "—"}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-center gap-2 mt-4">
                    <ButtonEdit
                      onClick={() => openEditModal(grade)}
                      hoverLabel="Éditer"
                    />
                    <ButtonDelete
                      onClick={() => deleteGradeHandler(grade.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {filteredGrades.length === 0 && !loading && (
            <div className="text-center text-slate-600 dark:text-slate-300 py-6">
              Aucun résultat avec ces filtres.
            </div>
          )}
          {loading && (
            <div className="py-6">
              <Loader />
            </div>
          )}
        </div>
      </div>
      </div>

      <CreateGradeModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        refreshList={refreshGradesAndHistories}
      />
      <EditGradeModal
        isOpen={isModalOpen}
        onClose={closeEditModal}
        grade={editableGrade}
        refreshList={refreshGradesAndHistories}
      />

      <ToastContainer />
    </section>
  );
}

export default GradeCNPage;
